/**
 * Department Management Component
 * 
 * Main component for managing departments. Handles CRUD operations,
 * URL-based filtering, and coordinates between list and form views.
 * 
 * @component
 * @example
 * return <Department />
 */

import React, { useEffect, useState, useCallback } from 'react';
import { message } from 'antd';
import { useSearchParams } from 'react-router-dom';
import CustomContainer from '../common/CustomContainer';
import DepartmentForm from './department-form';
import DepartmentList from './department-list';
import DepartmentDetailsTabbed from './DepartmentDetailsTabbed';
import DepartmentService from '../../services/department-service';
import { getFacilities } from '../../utils/apiClient';
import { cleanPhoneNumber, formatPhoneNumberForEdit } from '../../utils/helpers/format-phone-number';
import { useURLFiltering, useCRUDOperations } from '../../custom-hooks';

/**
 * Department Management Main Component
 */
const Department = () => {
  const [facilityMap, setFacilityMap] = useState({});
  const [facilities, setFacilities] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [departmentDetailsLoading, setDepartmentDetailsLoading] = useState(false);

  // Check if we're in department details mode
  const departmentId = searchParams.get('departmentId');
  const facilityId = searchParams.get('facilityId');
  const isDepartmentDetailsMode = !!departmentId;

  // Use custom hooks for common functionality
  const {
    selectedFilters,
    updateFilters,
    getAPIParams
  } = useURLFiltering('department');

  const {
    data: departments,
    loading,
    renderType,
    initialValues,
    fetchData,
    refreshData,
    handleSubmit: handleCRUDSubmit,
    handleEdit: handleCRUDEdit,
    handleAdd,
    handleCancel,
    setRenderType
  } = useCRUDOperations(DepartmentService, 'department');

  console.log('departmentsDebugger', departments);

  /**
   * Fetches facilities and creates facility map
   */
  const fetchFacilities = async () => {
    try {
      const facilitiesData = await getFacilities();
      console.log('🔄 Raw Facilities Response:', facilitiesData);
      console.log('🔄 Is Array?', Array.isArray(facilitiesData));
      console.log('🔄 Type:', typeof facilitiesData);
      
      if (Array.isArray(facilitiesData)) {
        setFacilities(facilitiesData); // Store full facilities array for form
        
        const map = {};
        facilitiesData.forEach((fac, index) => { 
          console.log(`🏢 Facility ${index}:`, fac);
          console.log(`🔑 ID: ${fac._id}, Name: ${fac.facilityName}`);
          map[fac._id] = fac.facilityName; 
        });
        console.log('🗺️ Final Facility Map:', map);
        setFacilityMap(map);
      } else {
        console.warn('❌ Facilities is not an array:', facilitiesData);
        setFacilities([]);
        setFacilityMap({});
      }
    } catch (error) {
      console.error('❌ Error fetching facilities:', error);
      setFacilities([]);
      setFacilityMap({});
    }
  };

  /**
   * Handles filter changes with API integration
   * 
   * @param {Object} newFilters - New filter values
   */
  const handleFiltersChange = (newFilters) => {
    try {
      updateFilters(newFilters); // This will trigger useEffect to call fetchData
    } catch (error) {
      console.error('Error applying filters:', error);
      message.error('Failed to apply filters. Please try again.');
    }
  };

  /**
   * Refreshes department data with current filters
   * 
   * @returns {Promise<Array>} Refreshed department data
   */
  const handleRefresh = async () => {
    const apiParams = getAPIParams(selectedFilters);
    return await refreshData(apiParams);
  };

  /**
   * Handles editing a department
   * 
   * @param {Object} department - Department to edit
   */
  const handleEdit = (department) => {
    const mapDepartmentToFormValues = (department) => ({
      name: department.name || '',
      facilityId: department.facilityId || '',
      faxNumber: department.faxNumber ? formatPhoneNumberForEdit(department.faxNumber) : '',
      isActive: department.isActive !== undefined ? department.isActive : true
    });
    handleCRUDEdit(department, mapDepartmentToFormValues);
  };

  /**
   * Handles form submission for create/update
   * 
   * @param {Object} data - Form data to submit
   */
  const handleSubmit = async (data) => {
    // Transform form data to API format with cleaned phone numbers
    const departmentData = {
      name: data.name,
      facilityId: data.facilityId,
      faxNumber: data.faxNumber ? cleanPhoneNumber(data.faxNumber) : null,
      ...(data.isActive !== undefined && { isActive: data.isActive })
    };

    const result = await handleCRUDSubmit(departmentData, handleRefresh);
    return result;
  };

  /**
   * Handles add new department
   */
  const handleAddNew = () => {
    handleAdd();
  };

  /**
   * Handles closing department details and returning to list
   */
  const handleCloseDepartmentDetails = useCallback(() => {
    setSearchParams(prevParams => {
      const newParams = new URLSearchParams(prevParams);
      newParams.delete('departmentId');
      newParams.delete('facilityId');
      return newParams;
    });
    setSelectedDepartment(null);
  }, [setSearchParams]);

  /**
   * Loads department details when departmentId is present in URL
   */
  const loadDepartmentDetails = useCallback(async (deptId) => {
    if (!deptId) return;

    try {
      setDepartmentDetailsLoading(true);
      console.log('🏢 Loading department details for:', deptId);
      
      const result = await DepartmentService.getById(deptId, {
        includeStaff: true,
        includeRenderingProviders: true
      });
      
      if (result.success && result.data) {
        setSelectedDepartment(result.data);
        console.log('✅ Department details loaded:', result.data);
      } else {
        throw new Error(result.message || 'Department not found');
      }
    } catch (error) {
      console.error('❌ Error loading department details:', error);
      message.error('Failed to load department details. Please try again.');
      handleCloseDepartmentDetails();
    } finally {
      setDepartmentDetailsLoading(false);
    }
  }, [handleCloseDepartmentDetails]);

  // Load department details when departmentId changes
  useEffect(() => {
    if (departmentId) {
      loadDepartmentDetails(departmentId);
    }
  }, [departmentId, loadDepartmentDetails]);

  // Initialize data on component mount
  useEffect(() => {
    const apiParams = getAPIParams(selectedFilters);
    fetchFacilities();
    fetchData(apiParams);
  }, [selectedFilters, fetchData, getAPIParams]);

  // Get facility information for selected department
  const selectedFacility = selectedDepartment?.facilityId 
    ? facilities.find(f => f._id === selectedDepartment.facilityId)
    : null;

  return (
    <CustomContainer containerWidth={
      isDepartmentDetailsMode ? '95%' : 
      renderType === 'list' ? '90%' : '80%'
    }>
      {isDepartmentDetailsMode ? (
        <DepartmentDetailsTabbed 
          department={selectedDepartment}
          facility={selectedFacility}
          facilityId={facilityId}
          loading={departmentDetailsLoading}
          onClose={handleCloseDepartmentDetails}
          onEdit={() => {
            // TODO: Implement edit functionality
            console.log('Edit department:', selectedDepartment);
          }}
        />
      ) : renderType === 'list' ? (
        <DepartmentList 
          setRenderType={setRenderType} 
          renderType={renderType} 
          departments={departments} 
          loading={loading} 
          onEdit={handleEdit}
          onAdd={handleAddNew}
          facilityMap={facilityMap}
          facilities={facilities}
          onRefresh={handleRefresh}
          selectedFilters={selectedFilters}
          onFiltersChange={handleFiltersChange}
        />
      ) : (
        <DepartmentForm 
          department={initialValues}
          onClose={handleCancel}
          setRenderType={setRenderType}
          renderType={renderType}
          facilities={facilities}
          handleSubmit={handleSubmit}
        />
      )}
    </CustomContainer>
  );
};

export default Department;
